import type { AlarmCategory } from '@ctcells/i18n'

/**
 * 알람 분류(`Alarm.category`) 어휘와 화면 순서.
 *
 * 🔴 **출처는 서버 카탈로그다** — `error_codes.yaml` `category_policy` 5키이고, 서버는
 *    `known_categories()`(`ctcells-core app/services/alarms.py:285`)로 코드들이 실제로 쓰는 값을 센다.
 *    2026-09-26 실측: `GET /alarms?category=bogus` 의 422 `context.known` 이 이 다섯이다.
 * ⚠ 계약은 이 값을 **열거하지 않는다**(`Alarm.category: string`). 그래서 이 목록은 «화면이 아는 것»일
 *   뿐이고, 모르는 값이 응답에 오면 선택기가 그 값을 **원값 이름으로 덧붙인다**(`categoryOptionsOf`).
 * 순서: 사람 손이 급한 쪽부터 — 즉시 정지 → 서비스 호출 → 조치 필요 → 자동 복구 → 일시 장애.
 */
export const ALARM_CATEGORIES: readonly AlarmCategory[] = [
  'critical',
  'unrecoverable',
  'user_recoverable',
  'recoverable',
  'transient',
]

/**
 * 분류 선택기의 「전체」 값. 🔴 「분류 없음」 선택지는 **없다** — 값이 안 온 알람(옛 적재분)은
 * 「전체」에만 보인다. 부재는 「모른다」이지 한 분류가 아니다.
 */
export const ALL_CATEGORIES = 'all'

/** 값이 «있는» 분류만 돌려준다 — `undefined` · `''` 는 「모른다」라 어느 분류에도 들지 않는다 */
export function categoryOf(alarm: { category?: string | undefined }): string | undefined {
  const value = alarm.category
  return value === undefined || value === '' ? undefined : value
}

/** 선택기 선택지 — 아는 다섯 + 응답에 실제로 온 «모르는» 값(원값 그대로) */
export function categoryOptionsOf(
  alarms: readonly { category?: string | undefined }[],
): readonly string[] {
  const known = new Set<string>(ALARM_CATEGORIES)
  const extra = new Set<string>()
  for (const alarm of alarms) {
    const value = categoryOf(alarm)
    if (value !== undefined && !known.has(value)) extra.add(value)
  }
  return [...ALARM_CATEGORIES, ...[...extra].sort()]
}
